import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva(
  'inline-flex items-center text-muted-foreground',
  {
    variants: {
      variant: {
        segmented: 'justify-center rounded-lg border border-border/50 bg-secondary/50',
        line: 'justify-start bg-transparent relative border-b border-border',
      },
      size: {
        sm: 'h-9',
        md: 'h-10',
        lg: 'h-11',
      },
    },
    defaultVariants: {
      variant: 'segmented',
      size: 'md',
    },
  }
);

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, size, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant, size }), className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-normal text-muted-foreground/70 ring-offset-background transition-all duration-200 ease-in-out hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground data-[state=active]:font-medium',
  {
    variants: {
      variant: {
        segmented: 'rounded-lg data-[state=active]:bg-background data-[state=active]:h-full data-[state=active]:shadow-sm',
        line: 'rounded-none relative data-[state=active]:bg-transparent after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:w-full after:rounded-full after:bg-transparent after:transition-all after:duration-200 data-[state=active]:after:bg-foreground',
      },
      size: {
        sm: 'px-2 text-xs',
        md: 'px-3 text-sm', 
        lg: 'px-4 text-base',
      },
    },
    compoundVariants: [
      {
        variant: 'segmented',
        size: 'sm',
        class: 'py-1',
      },
      {
        variant: 'segmented', 
        size: 'md',
        class: 'py-1.5',
      },
      {
        variant: 'segmented',
        size: 'lg', 
        class: 'py-2',
      },
      {
        variant: 'line',
        size: 'sm',
        class: 'pt-1 pb-2.5',
      },
      {
        variant: 'line',
        size: 'md',
        class: 'pt-1.5 pb-3',
      },
      {
        variant: 'line',
        size: 'lg',
        class: 'pt-2 pb-4',
      },
    ],
    defaultVariants: {
      variant: 'segmented',
      size: 'md',
    },
  }
);

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant, size, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant, size }), className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
